/**
 * /onboarding/$token — the six-step async onboarding flow (T-032 + T-033).
 *
 * PRD-003 PR-06, PR-52, DATA-02, DATA-18, US-04. ARCH-007 (TanStack
 * file-based routing). The route loads the onboarding context for the token,
 * enforces the entry guard, then renders OnboardingFlow.
 *
 * Token-gated AND auth-gated. Per ARCH-012 the subscriber signs in before
 * checkout, so by onboarding time they are authenticated; RLS scopes the
 * onboarding row to the signed-in owner. The token in the path is the
 * lookup / anti-enumeration key, not a replacement for auth.
 *
 * AC (T-032): "Token-gated route; bad/expired token redirects to /." A token
 * that resolves to no row (bad, expired, or not this subscriber's) sends the
 * visitor to `/`. An unauthenticated visitor is sent to sign-in (they cannot
 * have reached a paid onboarding state without an account; this is the honest
 * recovery path, not a leak). A not-yet-paid subscriber (status != 'active',
 * e.g. ARCH-012 'pending') is also sent to `/` — onboarding does not begin
 * before payment.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { loadOnboardingByToken } from '@/lib/onboarding';
import { OnboardingFlow } from '@/components/onboarding/OnboardingFlow';

export const Route = createFileRoute('/onboarding/$token')({
  loader: async ({ params }) => {
    const result = await loadOnboardingByToken(params.token);

    if (result.kind === 'unauthenticated') {
      throw redirect({
        to: '/auth/sign-in',
        search: { redirect: `/onboarding/${params.token}` },
      });
    }
    if (
      result.kind === 'not-found' ||
      result.kind === 'not-active' ||
      result.kind === 'error'
    ) {
      // Bad/expired token, not this subscriber's, not yet paid, or a read
      // error: send to the root. The onboarding surface never explains which.
      throw redirect({ to: '/' });
    }

    return { token: params.token, sections: result.context.sections };
  },
  component: OnboardingRoute,
});

function OnboardingRoute() {
  const { token, sections } = Route.useLoaderData();
  return <OnboardingFlow token={token} initialSections={sections} />;
}
